import { Box } from '@mantine/core';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { matchRoutes, useNavigate } from 'react-router';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { Show } from '../../../../react-commons/components/control-flow/Show';
import { createRouter, type RouteDefinitions } from '../../../../react-commons/router/createRouter';
import type { ExtractRouterTypes } from '../../../../react-commons/router/extractRouterTypes';
import { IdSchema } from '../../resource/resource-base/baseSchemas';
import type { RootState } from '../../store/rootReducer';
import type { Path, Route } from './route';
import { checkUnsavedData } from '../util/checkUnsavedData';

const routeDefinitions = {
    ['/']: {
        routeParams: z.strictObject({}),
        searchParams: z.strictObject({}),
    },
    ['/auth/oidc/callback/login']: {
        routeParams: z.strictObject({}),
        searchParams: z.object({}),
    },
    ['/admin-panel']: {
        routeParams: z.strictObject({}),
        searchParams: z.strictObject({}),
    },
    ['/admin-panel/system-settings']: {
        routeParams: z.strictObject({}),
        searchParams: z.strictObject({}),
    },
    ['/admin-panel/system-right-management']: {
        routeParams: z.strictObject({}),
        searchParams: z.strictObject({}),
    },
    ['/admin-panel/system-announcements']: {
        routeParams: z.strictObject({}),
        searchParams: z.strictObject({}),
    },
    ['/project-user-management/:id']: {
        routeParams: z.strictObject({
            id: IdSchema,
        }),
        searchParams: z.strictObject({}),
    },
    ['/projects']: {
        routeParams: z.strictObject({}),
        searchParams: z.strictObject({
            selected: IdSchema.optional(),
        }),
    },
    ['/project/:id/active-version/:versionId']: {
        routeParams: z.strictObject({
            id: IdSchema,
            versionId: IdSchema,
        }),
        searchParams: z.strictObject({}),
    },
    ['/project/:id/active-version/:versionId/standard/:standardId/use-case/:useCaseId']: {
        routeParams: z.strictObject({
            id: IdSchema,
            versionId: IdSchema,
            standardId: IdSchema,
            useCaseId: IdSchema,
        }),
        searchParams: z.strictObject({}),
    },
    ['/project/:id']: {
        routeParams: z.strictObject({
            id: IdSchema,
        }),
        searchParams: z.strictObject({}),
    },
    ['/project/:id/active-version/:versionId/standard/:standardId/use-case/:useCaseId/workflow-representations']: {
        routeParams: z.strictObject({
            id: IdSchema,
            versionId: IdSchema,
            standardId: IdSchema,
            useCaseId: IdSchema,
        }),
        searchParams: z.strictObject({
            selected: IdSchema.optional(),
            redirected: z.string().optional()
        }),
    },
    ['/project/:id/published-use-case/:useCaseObjectId/published-workflow-representation/:workflowObjectId']: {
        routeParams: z.strictObject({
            id: IdSchema,
            useCaseObjectId: IdSchema,
            workflowObjectId: IdSchema,
        }),
        searchParams: z.strictObject({
            selected: IdSchema.optional(),
            redirected: z.string().optional(),
            publishment: z.string().optional()
        }),
    },
    ['/project/:id/published-use-case/:useCaseObjectId']: {
        routeParams: z.strictObject({
            id: IdSchema,
            useCaseObjectId: IdSchema,
        }),
        searchParams: z.strictObject({
            selected: IdSchema.optional(),
            redirected: z.string().optional(),
            publishment: z.string().optional()
        }),
    },
    ['/project/:id/published-process-area/:processAreaObjectId/published-use-case/:useCaseObjectId/published-workflow-representation/:workflowObjectId']: {
        routeParams: z.strictObject({
            id: IdSchema,
            processAreaObjectId: IdSchema,
            useCaseObjectId: IdSchema,
            workflowObjectId: IdSchema,
        }),
        searchParams: z.strictObject({
            section: z.string().optional(),
            redirected: z.string().optional(),
            publishment: z.string().optional()
        }),
    },
    ['/project/:id/published-process-area/:processAreaObjectId/published-use-case/:useCaseObjectId']: {
        routeParams: z.strictObject({
            id: IdSchema,
            processAreaObjectId: IdSchema,
            useCaseObjectId: IdSchema,
        }),
        searchParams: z.strictObject({
            section: z.string().optional(),
            redirected: z.string().optional(),
            publishment: z.string().optional()
        }),
    },
    ['/project/:id/published-process-area/:processAreaObjectId']: {
        routeParams: z.strictObject({
            id: IdSchema,
            processAreaObjectId: IdSchema,
        }),
        searchParams: z.strictObject({
            section: z.string().optional(),
            redirected: z.string().optional(),
            publishment: z.string().optional()
        }),
    },
    ['/project/:id/active-version/:versionId/process-area/:processAreaId/standard/:standardId/use-case/:useCaseId/workflow-representation/:workflowId']: {
        routeParams: z.strictObject({
            id: IdSchema,
            versionId: IdSchema,
            processAreaId: IdSchema,
            standardId: IdSchema,
            useCaseId: IdSchema,
            workflowId: IdSchema,
        }),
        searchParams: z.strictObject({
            section: z.string().optional(),
            redirected: z.string().optional()
        }),
    },
    ['/project/:id/active-version/:versionId/process-area/:processAreaId/standard/:standardId/use-case/:useCaseId']: {
        routeParams: z.strictObject({
            id: IdSchema,
            versionId: IdSchema,
            processAreaId: IdSchema,
            standardId: IdSchema,
            useCaseId: IdSchema,
        }),
        searchParams: z.strictObject({
            section: z.string().optional(),
            redirected: z.string().optional()
        }),
    },
    ['/project/:id/active-version/:versionId/process-area/:processAreaId']: {
        routeParams: z.strictObject({
            id: IdSchema,
            versionId: IdSchema,
            processAreaId: IdSchema,
        }),
        searchParams: z.strictObject({
            section: z.string().optional(),
            redirected: z.string().optional()
        }),
    },
    ['/project/:id/active-version/:versionId/activities-model/:activitiesId']: {
        routeParams: z.strictObject({
            id: IdSchema,
            versionId: IdSchema,
            activitiesId: IdSchema,
        }),
        searchParams: z.strictObject({}),
    },
    ['/project/:id/active-version/:versionId/activities-model/:activitiesId/standard/:standardId/use-case/:useCaseId']: {
        routeParams: z.strictObject({
            id: IdSchema,
            versionId: IdSchema,
            activitiesId: IdSchema,
            standardId: IdSchema,
            useCaseId: IdSchema,
        }),
        searchParams: z.strictObject({}),
    },
    ['*']: {
        routeParams: z.strictObject({}),
        searchParams: z.strictObject({}),
    },
} as const satisfies RouteDefinitions<Path>;

type TypeofRouteDefinitions = typeof routeDefinitions;

const routes = Object.keys(routeDefinitions).map((path) => ({ path })) as Array<{ path: Path }>;
const useCurrentRoute = (): Path => {
    const location = useLocation();
    const match = matchRoutes(routes, location)?.at(0);

    return match!.route.path;
};

const { useMyNavigate, MyLink, useMyParams, createPathFromRouteInfo, openNewWindow, useValidateRoute } = createRouter<
    Path,
    Route,
    TypeofRouteDefinitions
>(routeDefinitions, {
    useCurrentRoute,
    useRouteParams: useParams,
    useSearchParams,
    useNavigate: () => {
        // cannot use selectIsDirty here because of circular dependency
        const isDirty = useSelector((state: RootState) => state.ui.status.isDirty);
        const navigate = useNavigate();

        return useCallback((to) => checkUnsavedData(isDirty, () => navigate(to)), [isDirty, navigate]);
    },
    Link: ({ to, onAuxClick, children }) => {
        // cannot use selectIsDirty here because of circular dependency
        const isDirty = useSelector((state: RootState) => state.ui.status.isDirty);
        const navigate = useNavigate();

        return (
            <Show
                when={!isDirty}
                fallback={
                    <Box
                        style={{ display: 'contents' }}
                        onClick={() => checkUnsavedData(isDirty, () => navigate(to))}
                        onAuxClick={onAuxClick}
                        title={`${window.location.host}${to.pathname}${to.search ? `?${to.search}` : ''}`}
                    >
                        {children}
                    </Box>
                }
            >
                <Link
                    style={{ textDecoration: 'none', color: 'inherit', display: 'contents' }}
                    to={to}
                    onAuxClick={onAuxClick}
                >
                    {children}
                </Link>
            </Show>
        );
    },
});

type RouteTypes<R extends Route> = ExtractRouterTypes<Path, R, TypeofRouteDefinitions>;

export type RouteInfo<R extends Route> = RouteTypes<R>['RouteInfo'];
export type SomeRouteInfo = RouteTypes<Route>['SomeRouteInfo'];
export type MyNavigate = RouteTypes<Route>['MyNavigate'];

export {
    useCurrentRoute,
    useMyNavigate,
    MyLink,
    useMyParams,
    createPathFromRouteInfo,
    openNewWindow,
    useValidateRoute,
};
